/**
 * Client-side user profile access from the non-httpOnly `user` cookie.
 *
 * Use this in client components and event handlers (e.g. express checkout `onAuthorized`).
 * For Server Components and route handlers, use `getUser()` from `./actions`.
 *
 * **Why not `getUser()` in express handlers?** Server actions POST to the current page URL.
 * On CMS/PLP routes (collections, PDP) that POST can 500 while the cart drawer is open.
 * The `user` cookie exposes the same core profile fields synchronously on the client.
 */

import { USER_COOKIE_NAME } from './cookies';
import { CookieUserSchema, type AuthUser } from './schemas';

/**
 * Read the logged-in user profile from `document.cookie`.
 *
 * @returns Full `AuthUser` shape with empty addresses when logged in, otherwise `null`
 *
 * @example
 * ```ts
 * const user = getUserFromClientCookie();
 * const email = user?.email ?? payerEmail;
 * ```
 */
export function getUserFromClientCookie(): AuthUser | null {
  if (globalThis.document === undefined) {
    return null;
  }

  const match = new RegExp(String.raw`(?:^|;\s*)${USER_COOKIE_NAME}=([^;]*)`).exec(
    globalThis.document.cookie
  );
  if (!match?.[1]) {
    return null;
  }

  try {
    const decoded = decodeURIComponent(match[1]);
    const parsed = CookieUserSchema.safeParse(JSON.parse(decoded));
    if (!parsed.success) {
      return null;
    }

    const coreUser = parsed.data;
    return {
      id: coreUser.id,
      email: coreUser.email,
      firstName: coreUser.firstName,
      lastName: coreUser.lastName,
      metadata: coreUser.metadata,
      addresses: [],
      defaultBillingAddress: null,
      defaultShippingAddress: null,
    };
  } catch {
    return null;
  }
}
